/**
 * Song API Service
 * Fetches Christian worship song lyrics from multiple free sources
 *
 * Sources:
 * 1. lrclib.net       - Free, no key, lyrics + time-synced (primary)
 * 2. Genius           - Client Access Token (search/discovery only)
 *    + lrclib direct  - Free, no key, fetches full lyrics from Genius metadata
 *    + lyrics.ovh     - Free, no key, second-choice lyrics source if lrclib has no match
 * 3. ChartLyrics      - Free, no key, XML-based (tertiary)
 * 4. Fallback DB      - Built-in 23+ worship songs (always works offline)
 */

// Genius: use Client Access Token from env (NOT Client ID or Client Secret)
// Client ID / Client Secret are for OAuth server-side flows only
const GENIUS_ACCESS_TOKEN = process.env.REACT_APP_GENIUS_ACCESS_TOKEN;
const GENIUS_BASE_URL = 'https://api.genius.com';

// lyrics.ovh - completely free, no API key required
const LYRICS_OVH_BASE_URL = 'https://api.lyrics.ovh/v1';

// lrclib.net - free, open-source, no key required
const LRCLIB_BASE_URL = 'https://lrclib.net/api';

// ChartLyrics API (free, no key required)
const CHARTLYRICS_BASE_URL = 'http://api.chartlyrics.com/apiv1.asmx';

// ---- Matching / ranking helpers ----
// lrclib returns loosely-related tracks in arbitrary order, so results are
// ranked locally instead of trusting the first hit.

const VERSION_WORDS = [
  'remix', 'karaoke', 'instrumental', 'cover', 'tribute', 'live', 'acoustic',
  'piano', 'edit', 'radio', 'lullaby', 'medley', 'demo', 'slowed', 'reverb', 'nightcore'
];
const STOP_WORDS = new Set(['the', 'a', 'an', 'of', 'and']);

// Lowercase, strip accents, "(parentheticals)", "feat. ..." and punctuation.
export const normalizeText = (str = '') => str
  .toLowerCase()
  .normalize('NFD').replace(/\p{M}/gu, '')
  .replace(/\(.*?\)|\[.*?\]/g, ' ')
  .replace(/\b(feat|ft|featuring)\b.*$/, ' ')
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const tokensOf = (str) => normalizeText(str).split(' ').filter(t => t && !STOP_WORDS.has(t));

const hasVersionWord = (str) => {
  const words = (str || '').toLowerCase().split(/[^a-z]+/);
  return VERSION_WORDS.some(w => words.includes(w));
};

// 1 = same title, 0.7 = one title contains the other, else token overlap (<0.7).
export const titleSimilarity = (a, b) => {
  const ta = tokensOf(a);
  const tb = tokensOf(b);
  if (ta.length === 0 || tb.length === 0) return 0;
  if (ta.join(' ') === tb.join(' ')) return 1;
  const setB = new Set(tb);
  const overlap = ta.filter(t => setB.has(t)).length;
  if (overlap === Math.min(ta.length, tb.length)) return 0.7;
  return (overlap / (ta.length + tb.length - overlap)) * 0.69;
};

const artistSimilarity = (want, got) => {
  const tw = tokensOf(want);
  if (tw.length === 0) return 0;
  const setG = new Set(tokensOf(got));
  return tw.filter(t => setG.has(t)).length / tw.length;
};

// Rank a pool of songs for a free-text query ("oceans hillsong").
const rankForQuery = (songs, query) => {
  const qTokens = tokensOf(query);
  return songs
    .map(song => {
      const hay = new Set([...tokensOf(song.title), ...tokensOf(song.artist)]);
      const matched = qTokens.length
        ? qTokens.filter(t => hay.has(t)).length / qTokens.length
        : 0;
      let score = matched * 10;
      if (titleSimilarity(query, song.title) === 1) score += 3;
      // Tie-break: prefer titles without extra words the user didn't say
      // ("Oceans" over "Oceans Will Part" for "oceans hillsong").
      const qSet = new Set(qTokens);
      score -= 0.4 * tokensOf(song.title).filter(t => !qSet.has(t)).length;
      if (hasVersionWord(song.title) && !hasVersionWord(query)) score -= 3;
      if (song.lyrics && song.lyrics.length > 100) score += 0.5;
      return { song, score, matched };
    })
    .sort((x, y) => y.score - x.score);
};

// Score a candidate against a known title (+ optional artist).
const scoreAgainst = (want, song) => {
  const sim = titleSimilarity(want.title, song.title);
  let score = sim * 10 + artistSimilarity(want.artist, song.artist) * 3;
  if (hasVersionWord(song.title) && !hasVersionWord(want.title)) score -= 3;
  if (song.lyrics && song.lyrics.length > 100) score += 0.5;
  return { sim, score };
};

/**
 * Resolve a known song (title + optional artist, e.g. from the AI) to real
 * lyrics. Returns null unless a result genuinely matches the title, so the
 * caller never shows an unrelated song.
 */
export const findSong = async (title, artist = '') => {
  const cleanTitle = title.replace(/\(.*?\)|\[.*?\]/g, ' ').replace(/\s+/g, ' ').trim() || title;
  const want = { title, artist };
  const attempts = [
    artist && { track_name: cleanTitle, artist_name: artist },
    { track_name: cleanTitle },
    { q: `${cleanTitle} ${artist}`.trim() }
  ].filter(Boolean);

  const pool = [];
  const best = () => pool
    .map(song => ({ song, ...scoreAgainst(want, song) }))
    .sort((x, y) => y.score - x.score)[0];

  for (const params of attempts) {
    try {
      pool.push(...await lrclibSearch(params));
    } catch (error) {
      console.error('lrclib lookup error:', error);
    }
    const top = best();
    if (top && top.sim === 1 && (!artist || artistSimilarity(artist, top.song.artist) > 0)) break;
  }

  // Curated built-in songs are exact, so they compete on equal terms.
  pool.push(...getFallbackSongs('').map(song => ({ ...song, source: 'Built-in library' })));

  const top = best();
  return top && top.sim >= 0.7 ? top.song : null;
};

/**
 * Search for Christian worship songs
 * @param {string} query - Song title or artist name
 * @returns {Promise<Array>} Array of song results
 */
export const searchChristianSongs = async (query) => {
  try {
    const results = await Promise.allSettled([
      searchViaLrclib(query),
      searchViaGeniusWithLyrics(query),
      searchViaChartLyrics(query)
    ]);

    const allSongs = [];
    results.forEach(result => {
      if (result.status === 'fulfilled' && Array.isArray(result.value)) {
        allSongs.push(...result.value);
      }
    });

    // Curated songs join the pool only when they genuinely match the query.
    const curated = getFallbackSongs(query)
      .map(song => ({ ...song, source: 'Built-in library' }));
    const curatedMatches = rankForQuery(curated, query)
      .filter(r => r.matched >= 0.5)
      .map(r => r.song);

    // Rank by relevance first so the best version of a title survives dedupe.
    const ranked = rankForQuery([...allSongs, ...curatedMatches], query).map(r => r.song);

    const uniqueSongs = [];
    const seenTitles = new Set();
    ranked.forEach(song => {
      const key = song.title.toLowerCase().trim();
      if (!seenTitles.has(key)) {
        seenTitles.add(key);
        uniqueSongs.push(song);
      }
    });

    if (uniqueSongs.length === 0) {
      return getFallbackSongs(query);
    }

    return uniqueSongs.slice(0, 10);
  } catch (error) {
    console.error('Error searching songs:', error);
    return getFallbackSongs(query);
  }
};

/**
 * lrclib.net — free, no key, returns lyrics in one call
 * Docs: https://lrclib.net/docs
 */
async function lrclibSearch(params) {
  const search = typeof params === 'string' ? { q: params } : params;
  const url = `${LRCLIB_BASE_URL}/search?${new URLSearchParams(search).toString()}`;
  const options = { headers: { 'Lrclib-Client': 'SleekHear Church App' } };

  // lrclib occasionally answers 503 "server busy" — retry once.
  let response = await fetch(url, options);
  if (response.status === 503) {
    await new Promise(resolve => setTimeout(resolve, 600));
    response = await fetch(url, options);
  }
  if (!response.ok) return [];
  const tracks = await response.json();
  const songs = [];
  for (const track of tracks.slice(0, 15)) {
    const lyrics = track.plainLyrics || track.syncedLyrics || null;
    if (!lyrics) continue;
    songs.push({
      title: track.trackName,
      artist: track.artistName,
      lyrics: lyrics,
      source: 'lrclib'
    });
  }
  return songs;
}

async function searchViaLrclib(query) {
  try {
    // Try the exact query first — appending genre-bias words can cause
    // lrclib's search to miss an otherwise-perfect title/artist match.
    const exact = await lrclibSearch(query);
    if (exact.length > 0) return exact;

    // Fall back to a genre-biased search for vague/generic queries
    // (e.g. a single word) where narrowing actually helps.
    return await lrclibSearch(query + ' christian worship');
  } catch (error) {
    console.error('lrclib error:', error);
    return [];
  }
}

/**
 * Genius (search) + lrclib/lyrics.ovh (lyrics fetch)
 *
 * Genius API uses the Client Access Token as a Bearer token.
 * Client ID and Client Secret are NOT used here — they are for
 * server-side OAuth flows only.
 *
 * Genius does not return lyric text (only metadata + page URL), so
 * lyrics are fetched separately: lrclib's direct lookup first, then
 * lyrics.ovh if lrclib has no match for that artist/title.
 */
async function searchViaGeniusWithLyrics(query) {
  if (!GENIUS_ACCESS_TOKEN || GENIUS_ACCESS_TOKEN === 'your-genius-client-access-token-here') {
    return [];
  }

  try {
    // Step 1: Search Genius for song metadata
    const searchResponse = await fetch(
      `${GENIUS_BASE_URL}/search?q=${encodeURIComponent(query + ' worship christian')}`,
      {
        headers: {
          'Authorization': `Bearer ${GENIUS_ACCESS_TOKEN}`
        }
      }
    );

    if (!searchResponse.ok) return [];

    const searchData = await searchResponse.json();
    const hits = searchData.response?.hits?.slice(0, 5) || [];
    const songs = [];

    // Step 2: For each result, fetch lyrics from lyrics.ovh (free, no key)
    for (const hit of hits) {
      const song = hit.result;
      const artist = song.primary_artist?.name || '';
      const title = song.title || '';

      if (!artist || !title) continue;

      const lyrics = await fetchLyricsFromLrclib(artist, title) || await fetchLyricsOvh(artist, title);

      songs.push({
        title,
        artist,
        lyrics: lyrics || 'Lyrics not available',
        source: lyrics ? 'Genius + lrclib' : 'Genius'
      });
    }

    return songs;
  } catch (error) {
    console.error('Genius lyrics lookup error:', error);
    return [];
  }
}

/**
 * lrclib.net — direct artist/title lookup (free, no key)
 * More reliable than fuzzy search for a known artist + title pair.
 */
async function fetchLyricsFromLrclib(artist, title) {
  try {
    const response = await fetch(
      `${LRCLIB_BASE_URL}/get?artist_name=${encodeURIComponent(artist)}&track_name=${encodeURIComponent(title)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data.plainLyrics || data.syncedLyrics || null;
  } catch {
    return null;
  }
}

/**
 * lyrics.ovh — fetch full lyrics by artist + title
 * Free API, no key required: GET /v1/{artist}/{title}
 */
async function fetchLyricsOvh(artist, title) {
  try {
    const response = await fetch(
      `${LYRICS_OVH_BASE_URL}/${encodeURIComponent(artist)}/${encodeURIComponent(title)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data.lyrics || null;
  } catch {
    return null;
  }
}

/**
 * ChartLyrics (free, no key, XML — limited but works)
 */
async function searchViaChartLyrics(query) {
  try {
    const response = await fetch(
      `${CHARTLYRICS_BASE_URL}/SearchLyric?lyricText=${encodeURIComponent(query)}`
    );

    if (!response.ok) return [];

    const text = await response.text();
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, 'application/xml');
    const items = xml.querySelectorAll('SearchLyricResult');
    const songs = [];

    for (const item of Array.from(items).slice(0, 5)) {
      const title = item.querySelector('Song')?.textContent;
      const artist = item.querySelector('Artist')?.textContent;
      const lyricId = item.querySelector('LyricId')?.textContent;
      const lyricCheckSum = item.querySelector('LyricCheckSum')?.textContent;

      if (!title || !artist || !lyricId) continue;

      // Fetch full lyrics using the ID
      const lyricsResponse = await fetch(
        `${CHARTLYRICS_BASE_URL}/GetLyric?lyricId=${lyricId}&lyricCheckSum=${lyricCheckSum}`
      );

      let lyrics = 'Lyrics not available';
      if (lyricsResponse.ok) {
        const lyricsXml = parser.parseFromString(await lyricsResponse.text(), 'application/xml');
        lyrics = lyricsXml.querySelector('Lyric')?.textContent || lyrics;
      }

      songs.push({ title, artist, lyrics, source: 'ChartLyrics' });
    }

    return songs;
  } catch (error) {
    console.error('ChartLyrics error:', error);
    return [];
  }
}

/**
 * Expanded fallback database with popular Christian worship songs
 * Used when all APIs fail or are unavailable (works fully offline)
 */
function getFallbackSongs(query) {
  const normalizedQuery = query.toLowerCase();

  const fallbackDatabase = [
    // Hillsong
    { artist: 'Hillsong Worship', title: 'What A Beautiful Name', lyrics: 'You were the Word at the beginning\nOne with God the Lord Most High\nYour hidden glory in creation\nNow revealed in You our Christ\n\nWhat a beautiful Name it is\nWhat a beautiful Name it is\nThe Name of Jesus Christ my King\nWhat a beautiful Name it is\nNothing compares to this\nWhat a beautiful Name it is\nThe Name of Jesus' },
    { artist: 'Hillsong United', title: 'Oceans (Where Feet May Fail)', lyrics: 'You call me out upon the waters\nThe great unknown where feet may fail\nAnd there I find You in the mystery\nIn oceans deep my faith will stand\n\nAnd I will call upon Your name\nAnd keep my eyes above the waves\nWhen oceans rise\nMy soul will rest in Your embrace\nFor I am Yours and You are mine' },
    { artist: 'Hillsong Worship', title: 'Cornerstone', lyrics: 'My hope is built on nothing less\nThan Jesus blood and righteousness\nI dare not trust the sweetest frame\nBut wholly trust in Jesus Name\n\nChrist alone cornerstone\nWeak made strong in the Saviour\'s love\nThrough the storm He is Lord\nLord of all' },
    { artist: 'Hillsong United', title: 'Mighty to Save', lyrics: 'Everyone needs compassion\nA love that\'s never failing\nLet mercy fall on me\nEveryone needs forgiveness\nThe kindness of a Saviour\nThe hope of nations\n\nSaviour He can move the mountains\nMy God is mighty to save\nHe is mighty to save\nForever author of salvation\nHe rose and conquered the grave\nJesus conquered the grave' },

    // Bethel Music
    { artist: 'Bethel Music', title: 'Goodness of God', lyrics: 'I love You Lord\nOh Your mercy never fails me\nAll my days\nI\'ve been held in Your hands\nFrom the moment that I wake up\nUntil I lay my head\nI will sing of the goodness of God\n\nAll my life You have been faithful\nAll my life You have been so so good\nWith every breath that I am able\nI will sing of the goodness of God' },
    { artist: 'Bethel Music', title: 'Reckless Love', lyrics: 'Before I spoke a word You were singing over me\nYou have been so so good to me\nBefore I took a breath You breathed Your life in me\nYou have been so so kind to me\n\nOh the overwhelming never-ending reckless love of God\nOh it chases me down fights til I\'m found leaves the ninety-nine\nI couldn\'t earn it I don\'t deserve it still You give Yourself away\nOh the overwhelming never-ending reckless love of God' },
    { artist: 'Bethel Music', title: 'No Longer Slaves', lyrics: 'You unravel me with a melody\nYou surround me with a song\nOf deliverance from my enemies\nTil all my fears are gone\n\nI\'m no longer a slave to fear\nI am a child of God\nI\'m no longer a slave to fear\nI am a child of God' },

    // Chris Tomlin
    { artist: 'Chris Tomlin', title: 'How Great Is Our God', lyrics: 'The splendor of the King\nClothed in majesty\nLet all the earth rejoice\nAll the earth rejoice\nHe wraps Himself in light\nAnd darkness tries to hide\nAnd trembles at His voice\nTrembles at His voice\n\nHow great is our God\nSing with me\nHow great is our God\nAnd all will see how great\nHow great is our God' },
    { artist: 'Chris Tomlin', title: 'Good Good Father', lyrics: 'I\'ve heard a thousand stories of what they think You\'re like\nBut I\'ve heard the tender whisper of love in the dead of night\nYou tell me that You\'re pleased and that I\'m never alone\n\nYou\'re a good good Father\nIt\'s who You are it\'s who You are it\'s who You are\nAnd I\'m loved by You\nIt\'s who I am it\'s who I am it\'s who I am' },
    { artist: 'Chris Tomlin', title: 'Amazing Grace (My Chains Are Gone)', lyrics: 'Amazing grace how sweet the sound\nThat saved a wretch like me\nI once was lost but now I\'m found\nWas blind but now I see\n\nMy chains are gone I\'ve been set free\nMy God my Savior has ransomed me\nAnd like a flood His mercy reigns\nUnending love amazing grace' },

    // Elevation Worship
    { artist: 'Elevation Worship', title: 'O Come to the Altar', lyrics: 'Are you hurting and broken within\nOverwhelmed by the weight of your sin\nJesus is calling\nHave you come to the end of yourself\nDo you thirst for a drink from the well\nJesus is calling\n\nO come to the altar\nThe Father\'s arms are open wide\nForgiveness was bought with\nThe precious blood of Jesus Christ' },
    { artist: 'Elevation Worship', title: 'Graves Into Gardens', lyrics: 'I searched the world but it couldn\'t fill me\nMan\'s empty praise and treasures that fade\nAre never enough\nThen You came along and put me back together\nAnd every desire is now satisfied here in Your love\n\nYou turn mourning to dancing\nYou give beauty for ashes\nYou turn shame into glory\nYou\'re the only one who can\nYou turn graves into gardens\nYou turn bones into armies\nYou turn seas into highways\nYou\'re the only one who can' },
    { artist: 'Elevation Worship', title: 'Do It Again', lyrics: 'Walking around these walls\nI thought by now they\'d fall\nBut You have never failed me yet\nWaiting for change to come\nKnowing the battle\'s won\nFor You have never failed me yet\n\nYour promise still stands\nGreat is Your faithfulness faithfulness\nI\'m still in Your hands\nThis is my confidence You\'ve never failed me yet' },

    // Passion / Matt Redman
    { artist: 'Passion', title: '10,000 Reasons (Bless the Lord)', lyrics: 'Bless the Lord O my soul\nO my soul\nWorship His holy name\nSing like never before\nO my soul\nI\'ll worship Your holy name\n\nThe sun comes up it\'s a new day dawning\nIt\'s time to sing Your song again\nWhatever may pass and whatever lies before me\nLet me be singing when the evening comes' },
    { artist: 'Passion', title: 'One Thing Remains', lyrics: 'Higher than the mountains that I face\nStronger than the power of the grave\nConstant in the trial and the change\nOne thing remains one thing remains\n\nYour love never fails it never gives up\nIt never runs out on me' },

    // Kari Jobe
    { artist: 'Kari Jobe', title: 'The Blessing', lyrics: 'The Lord bless you and keep you\nMake His face shine upon you and be gracious to you\nThe Lord turn His face toward you and give you peace\n\nAmen amen amen\nAmen amen amen' },
    { artist: 'Kari Jobe', title: 'Revelation Song', lyrics: 'Worthy is the Lamb who was slain\nHoly holy is He\nSing a new song to Him who sits on\nHeaven\'s mercy seat\n\nHoly holy holy\nIs the Lord God Almighty\nWho was and is and is to come\nWith all creation I sing\nPraise to the King of kings\nYou are my everything\nAnd I will adore You' },

    // Matt Redman
    { artist: 'Matt Redman', title: '10,000 Reasons', lyrics: 'Bless the Lord O my soul\nO my soul\nWorship His holy name\nSing like never before\nO my soul\nI\'ll worship Your holy name' },
    { artist: 'Matt Redman', title: 'Blessed Be Your Name', lyrics: 'Blessed be Your name\nIn the land that is plentiful\nWhere Your streams of abundance flow\nBlessed be Your name\n\nBlessed be Your name\nWhen I\'m found in the desert place\nThough I walk through the wilderness\nBlessed be Your name' },

    // Others
    { artist: 'Phil Wickham', title: 'Living Hope', lyrics: 'How great the chasm that lay between us\nHow high the mountain I could not climb\nIn desperation I turned to heaven\nAnd spoke Your name into the night\n\nThen through the darkness Your loving kindness\nTore through the shadows of my soul\nThe work is finished the end is written\nJesus Christ my living hope' },
    { artist: 'Cory Asbury', title: 'Reckless Love', lyrics: 'Oh the overwhelming never-ending reckless love of God\nOh it chases me down fights til I\'m found leaves the ninety-nine' },
    { artist: 'Lauren Daigle', title: 'You Say', lyrics: 'You say I am loved when I can\'t feel a thing\nYou say I am strong when I think I am weak\nYou say I am held when I am falling short\nWhen I don\'t belong oh You say that I am Yours' },
    { artist: 'Brandon Lake', title: 'Gratitude', lyrics: 'All my words fall short\nI got nothing new\nHow could I express\nAll my gratitude\n\nI could sing these songs\nAs I often do\nBut every song must end\nAnd You never do' },
    { artist: 'Maverick City Music', title: 'Jireh', lyrics: 'I\'ll never be more loved than I am right now\nWasn\'t holding You up so there\'s nothing I can do to let You down\nIt doesn\'t take a trophy to make You proud\nI\'ll never be more loved than I am right now\n\nJireh You are enough\nJireh You are enough\nI will be content in every circumstance\nJireh You are enough' }
  ];

  if (!normalizedQuery) return fallbackDatabase;

  return fallbackDatabase.filter(song => {
    const titleMatch = song.title.toLowerCase().includes(normalizedQuery);
    const artistMatch = song.artist.toLowerCase().includes(normalizedQuery);
    const wordsMatch = normalizedQuery.split(' ').some(word =>
      word.length > 3 && (
        song.title.toLowerCase().includes(word) ||
        song.artist.toLowerCase().includes(word)
      )
    );
    return titleMatch || artistMatch || wordsMatch;
  });
}

/**
 * Search songs by lyrics
 * @param {string} lyrics - Partial lyrics sung/spoken
 * @returns {Promise<Array>} Array of matching songs
 */
export const searchSongsByLyrics = async (lyrics) => {
  try {
    // Try lrclib first (supports lyric search)
    const lrclibResults = await searchViaLrclib(lyrics);
    if (lrclibResults.length > 0) return lrclibResults;

    // Fall back to local database
    return searchByLyricsInFallback(lyrics);
  } catch (error) {
    console.error('Error searching by lyrics:', error);
    return searchByLyricsInFallback(lyrics);
  }
};

function searchByLyricsInFallback(searchLyrics) {
  const normalizedSearch = searchLyrics.toLowerCase().trim();
  const searchWords = normalizedSearch.split(' ').filter(w => w.length > 2);

  const allSongs = getFallbackSongs('');

  return allSongs.filter(song => {
    const songLyricsLower = song.lyrics.toLowerCase();

    const matchCount = searchWords.filter(word =>
      songLyricsLower.includes(word)
    ).length;

    if (matchCount >= Math.ceil(searchWords.length * 0.4)) return true;

    for (let i = 0; i < searchWords.length - 4; i++) {
      const phrase = searchWords.slice(i, i + 5).join(' ');
      if (songLyricsLower.includes(phrase)) return true;
    }

    return false;
  }).slice(0, 10);
}

const songApi = {
  searchChristianSongs,
  searchSongsByLyrics
};

export default songApi;
